import { trackEvent } from '@fs/zion-analytics'
import {
  convertToSLSRectangle,
  getNestedOrFloatingElementById,
  getNextUuid,
  getMostRecentFieldValue,
} from '@fs/zion-sls'
import { getHighlightSubjectPersonId } from './highlightSubject'

const MINSIZE = 0.01

/**
 * Find the highlight using the id
 * @param {Array} highlights - list of highlight objects
 * @param {String} id - the id of the highlight
 * @returns {*} highlight object
 */
export function getHighlightById(highlights, id) {
  if (!highlights || highlights.length === 0 || !id || typeof id !== 'string') return {}
  return highlights.find((h) => h.id === id)
}

/**
 * Highlights need to be at least a certain size. If a user clicks the screen, we will
 * default to that size.
 * @param {Object} rect - the rectangle object that has the x, y, width, and height
 * @returns {Obejct} the same rectangle object, but resized if necessary
 */
export function ensureMinimumRect(rect) {
  const width = rect.position.width < MINSIZE ? MINSIZE : rect.position.width
  const height = rect.position.height < MINSIZE ? MINSIZE : rect.position.height
  return { ...rect, position: { ...rect.position, width, height } }
}

/**
 * add a highlight object into an array of highlights or replace if already exists
 * @param {Array} oldHighlights - list of highlights as they were before change
 * @param {Object} highlight - the highlight that has been changed
 * @returns {Array} list of new highlights (copied)
 */
export function insertHighlight(oldHighlights, highlight) {
  const index = oldHighlights?.findIndex((h) => highlight.id === h.id)
  const newHighlights = [...oldHighlights]
  if (index > -1) newHighlights[index] = highlight
  else newHighlights.push(highlight)
  return [...newHighlights]
}

/**
 * add a highlight object into an array of highlights or replace if already exists
 * @param {Array} oldHighlights - list of highlights as they were before change
 * @param {Array} highlightsToInsert - the list of highlights that have been changed
 * @returns {Array} list of new highlights (copied)
 */
export function insertHighlights(oldHighlights, highlightsToInsert) {
  let newHighlights = [...oldHighlights]
  // Collect all the highlight inex values that need updating
  highlightsToInsert.forEach((highlight) => {
    newHighlights = insertHighlight(newHighlights, highlight)
  })
  return [...newHighlights]
}

/**
 * Set the display highlights.
 * @param {Array} highlights - Array of highlight objects. (like the reducer's editHighlights, or just regular reducer's 'highlights', or referenceEditHighlights... etc.)
 * @param {Object} api - API for managing the display of highlights. The api comes from the zion hook, useHighlights() and those variables have been made available through r2Context
 */
export const setDisplayHighlights = (highlights, api) => {
  try {
    api?.clear()
    if (!highlights || highlights.length === 0) return
    const arr = highlights.map((h) => [h.id, h])
    api?.addMany(arr)
  } catch (error) {
    console.error('ERROR: setDisplayHighlights', error)
  }
}

/**
 * @param {Rectangle} position immediately passed from the *Viewer (ComboViewer, MultiImageViewer, etc.) component after its creation to this callback function, which makes any extra edits to the rectangle, where the handling of the rectangle's addition to local state can be done
 * @param {Array} imagesData Sls data to search for field by fieldId
 * @param {String} currentImageArk Currently editing image ark
 * @param {Object} state r2State object
 * @param {Function} getHighlights SlsHighlightsHook setter function
 * @param {Function} displayHighlightsApi R2Highlights main display api
 * @param {Object} [activeFieldData] ActiveFieldInfo object from the SlsHighlightsContext
 */
export const onRectangleDrawnSlsHook = (
  position,
  imagesData,
  currentImageArk,
  state,
  getHighlights,
  displayHighlightsApi,
  activeFieldData
) => {
  let rect = { position: { ...position } }
  // This is the new way of getting a rectangle from any *Viewer component
  // It replaces requestRectangle, find the change list in the zion repo at /packages/image-viewer CHANGELOG.md 1.0.0

  trackEvent({
    event_name: 'click_action',
    link_name: `Highlight: Create`,
  })

  const activeField = activeFieldData
  rect = ensureMinimumRect(rect)
  rect.imageId = currentImageArk
  rect.fieldId = activeField?.id || state.activeFieldId
  rect.personIds = [getHighlightSubjectPersonId(state, activeField)]
  rect.fieldType = activeField?.fieldType ? activeField.fieldType : state.highlightsFieldInfo?.fieldType
  rect.elementType = activeField?.elementType
  rect.recordId = activeField?.recordId
  rect.id = `highlight-0-${getNextUuid()}`
  rect.change = 'add'
  rect.attached = true

  rect.originalHighlight = { ...convertToSLSRectangle(rect), ...rect }
  delete rect.originalHighlight.position
  displayHighlightsApi.add(rect.id, rect)
}

/**
 * @param {Rectangle} position immediately passed from the *Viewer (ComboViewer, MultiImageViewer, etc.) component after its creation to this callback function, which makes any extra edits to the rectangle, where the handling of the rectangle's addition to local state can be done
 * @param {Array} imagesData Sls data to search for field by fieldId
 * @param {String} currentImageArk Currently editing image ark
 * @param {Object} state r2State object
 * @param {Object} displayHighlightsApi Api for editing display highlights
 * @param {Function} dispatch Dispatcher to r2State
 * @param {Object} [activeFieldData] ActiveFieldInfo object from the SlsHighlightsContext
 * @param {Boolean} [useSlsHighlightsHookData=false] Flag to indicate if we should use the activeFieldData param or the state.activeFieldId to get the active field data. This is used for the new highlights panel
 * @param {Boolean} [shouldDispatchToState=true] Flag to indicate if we should dispatch the SET_CREATING_RECT action to the local state.
 */
export const onRectangleDrawn = (
  position,
  imagesData,
  currentImageArk,
  state,
  displayHighlightsApi,
  dispatch,
  activeFieldData,
  useSlsHighlightsHookData = false,
  shouldDispatchToState = true
) => {
  let rect = { position: { ...position } }
  // This is the new way of getting a rectangle from any *Viewer component
  // It replaces requestRectangle, find the change list in the zion repo at /packages/image-viewer CHANGELOG.md 1.0.0
  const currentImage = imagesData?.find((i) => i.partitionKey === currentImageArk) || []

  trackEvent({
    event_name: 'click_action',
    link_name: `Highlight: Create`,
  })

  const activeField = useSlsHighlightsHookData
    ? activeFieldData
    : getNestedOrFloatingElementById([currentImage], state.activeFieldId)
  rect = ensureMinimumRect(rect)
  rect.imageId = currentImageArk
  rect.fieldId = activeField?.id || state.activeFieldId
  rect.personIds = [getHighlightSubjectPersonId(state, activeField)]
  rect.fieldType = activeField?.fieldType ? activeField.fieldType : state.highlightsFieldInfo?.fieldType
  rect.elementType = activeField?.elementType
  rect.recordId = activeField?.recordId
  rect.id = `highlight-0-${getNextUuid()}`
  rect.change = 'add'
  rect.attached = true

  rect.originalHighlight = { ...convertToSLSRectangle(rect), ...rect }
  delete rect.originalHighlight.position
  displayHighlightsApi.add(rect.id, rect)

  // This turns off drawMode because drawMode is derived from R2State.creatingRect
  if (shouldDispatchToState) {
    dispatch({ type: 'SET_CREATING_RECT', value: false })
  }
}

export const searchForShareableHighlight = (highlights, fieldId, fieldValue, images) => {
  let sharedField = null
  const updatedHighlights = highlights.map((h) => {
    if (h.change && h.fieldId === fieldId && h.originalHighlight.fieldId && h.fieldId !== h.originalHighlight.fieldId) {
      const oldField = getNestedOrFloatingElementById(images, h.originalHighlight.fieldId)
      const oldFieldValue = getMostRecentFieldValue(oldField)
      if (oldFieldValue === fieldValue) {
        sharedField = oldField
        const newHighlight = {
          ...h,
          fieldId: sharedField.id,
        }
        delete newHighlight.change
        return newHighlight
      }
    }
    return h
  })

  return { sharedField, updatedHighlights }
}

/** Helper function to get the selected person's highlights */
export const isSelectedPersonHighlight = (highlight, selectedPersonId) =>
  Boolean(selectedPersonId && highlight.personIds.includes(selectedPersonId))
/** Helper function to get the active field highlights */
export const isActiveField = (highlight, activeFieldIds) =>
  Boolean(activeFieldIds?.length && activeFieldIds.includes(highlight.fieldId))
